import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, MenuItem, FormControlLabel, Switch, Button, Typography, Divider, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import { useGetQuestionOptionsQuery, useUpdateQuestionOptionMutation, useCreateQuestionOptionMutation, useUpdateQuizQuestionMutation, useGetRequirementsByPosteQuery } from '../../services/SkillsManagement';
import DeleteOption from './DeleteOption';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';

// Combined viewer/editor for a question and its options. Read-only by default with a single Edit button.
const UpdateOption = ({ question, posteId, onUpdated, onCancel }) => {
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [editMode, setEditMode] = useState(true);

  const [updateQuestion, { isLoading: savingQuestion }] = useUpdateQuizQuestionMutation();
  const [updateOption] = useUpdateQuestionOptionMutation();
  const [createOption] = useCreateQuestionOptionMutation();

  // Always refresh from Redux store to avoid stale data when reopening edit without unmount
  const propQId = question?.questionId ?? question?.id;
  const storeQuestion = useSelector((state) =>
    (state?.quizQuestions || state?.questionQuizReducer || [])
      .find((q) => Number(q?.questionId ?? q?.id) === Number(propQId))
  ) || question;

  const normalizedQ = useMemo(() => ({
    questionId: storeQuestion?.questionId ?? storeQuestion?.id,
    quizId: Number(storeQuestion?.quizId ?? 0),
    skillId: storeQuestion?.skillId != null ? Number(storeQuestion?.skillId) : '',
    questionType: storeQuestion?.questionType || 'single_choice',
    questionText: storeQuestion?.questionText || '',
    hint: storeQuestion?.hint ?? '',
    points: Number(storeQuestion?.points ?? 1),
    timeLimit: storeQuestion?.timeLimit != null ? Number(storeQuestion?.timeLimit) : 0,
    order: Number(storeQuestion?.order ?? 1),
    isRequired: Boolean(storeQuestion?.isRequired ?? true),
    explanation: storeQuestion?.explanation ?? '',
  }), [storeQuestion]);

  const [form, setForm] = useState(normalizedQ);
  useEffect(() => { setForm(normalizedQ); }, [normalizedQ]);

  const onChangeQ = (field) => (e) => {
    const value =
      field === 'isRequired'
        ? Boolean(e.target.checked)
        : ['points', 'timeLimit', 'order', 'skillId'].includes(field)
        ? Number(e.target.value)
        : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Fetch options for the question
  const qId = normalizedQ.questionId;
  const { data: optionsData, isLoading: optionsLoading } = useGetQuestionOptionsQuery(
    { questionId: Number(qId), page: 1, limit: 200 },
    { skip: !qId }
  );
  const initialOptions = useMemo(
    () => (((storeQuestion?.options && Array.isArray(storeQuestion.options) && storeQuestion.options.length > 0)
      ? storeQuestion.options
      : (optionsData?.data?.questionOptions || optionsData?.questionOptions || [])
    )).map((o, idx) => {
      const normalizedId = o?.questionOptionId ?? o?.optionId ?? o?.id;
      return {
        id: normalizedId,
        questionOptionId: o?.questionOptionId ?? o?.optionId ?? o?.id,
        optionId: o?.optionId ?? o?.questionOptionId ?? o?.id,
        questionId: o?.questionId ?? qId,
        optionText: o?.optionText || '',
        isCorrect: Boolean(o?.isCorrect),
        order: Number(o?.order ?? idx),
      };
    }),
    [optionsData, qId, storeQuestion]
  );
  const [options, setOptions] = useState(initialOptions);
  useEffect(() => { setOptions(initialOptions); }, [initialOptions]);

  const updateOpt = (idx, field, value) => setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  const removeOptLocal = (idx) => setOptions((prev) => prev.filter((_, i) => i !== idx).map((o, i) => ({ ...o, order: i })));
  const removeOptById = (optionId) =>
    setOptions((prev) =>
      prev
        .filter((o) => {
          const oid = o?.id ?? o?.questionOptionId ?? o?.optionId;
          return Number(oid) !== Number(optionId);
        })
        .map((o, i) => ({ ...o, order: i }))
    );

  const addOption = () =>
    setOptions((prev) => [
      ...prev,
      {
        id: undefined,
        questionOptionId: undefined,
        optionId: undefined,
        questionId: qId,
        optionText: '',
        isCorrect: false,
        order: prev.length,
      },
    ]);

  // Load skills for dropdown like Create/UpdateQuestion
  const { data: reqData, isLoading: skillsLoading, error: skillsError } = useGetRequirementsByPosteQuery(
    {
      posteId: Number(posteId),
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 1000 }),
      sorting: '',
    },
    { skip: !posteId }
  );
  const skills = Array.isArray(reqData?.requirements || reqData?.data?.requirements)
    ? (reqData?.requirements || reqData?.data?.requirements)
        .map((r, idx) => ({
          id: Number(r?.skill_id ?? r?.skillId ?? idx),
          name: r?.skill?.name || r?.skill_name || `Skill ${Number(r?.skill_id ?? r?.skillId ?? idx)}`,
        }))
        .filter((s) => Number.isFinite(s.id))
    : [];

  // Ensure select value remains valid against loaded options to prevent MUI out-of-range warnings
  useEffect(() => {
    const current = Number(form.skillId);
    const exists = skills.some((s) => s.id === current);
    if (!skillsLoading && !exists && form.skillId !== '') {
      setForm((prev) => ({ ...prev, skillId: '' }));
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[UpdateOption] Reset skillId because it is not in loaded skills', { current, skillsCount: skills.length });
      }
    }
  }, [skillsLoading, skills, form.skillId]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!qId) return;
    try {
      const numOrU = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));
      // 1) Update question
      const qBody = {
        // questionId is path param; do not send in body
        ...(Number(form.quizId) > 0 ? { quizId: Number(form.quizId) } : {}),
        skillId: numOrU(form.skillId),
        questionType: form.questionType,
        questionText: form.questionText.trim(),
        hint: form.hint?.trim() || undefined,
        points: numOrU(form.points),
        timeLimit: numOrU(form.timeLimit),
        order: numOrU(form.order),
        isRequired: Boolean(form.isRequired),
        explanation: form.explanation?.trim() || undefined,
      };
      const qRes = await updateQuestion({ id: Number(qId), body: qBody }).unwrap();
      const updatedQ = qRes?.data || qRes;
      dispatch({ type: actions.UPDATE_QUIZ_QUESTION, payload: updatedQ });

      // 2) Upsert options in parallel (updates for existing, creates for new)
      const existing = options.filter((o) => o?.id || o?.questionOptionId || o?.optionId);
      const news = options.filter((o) => !(o?.id || o?.questionOptionId || o?.optionId));

      const updateTasks = existing.map((o) =>
        updateOption({
          id: Number(o?.id ?? o?.questionOptionId ?? o?.optionId),
          body: {
            optionText: (o.optionText || '').trim(),
            isCorrect: Boolean(o.isCorrect),
            order: Number.isFinite(o.order) ? Number(o.order) : 0,
          },
        }).unwrap()
      );

      const createTasks = news.map((o, idx) =>
        createOption({
          body: {
            questionId: Number(qId),
            optionText: (o.optionText || '').trim(),
            isCorrect: Boolean(o.isCorrect),
            order: Number.isFinite(o.order) ? Number(o.order) : idx,
          },
        }).unwrap()
      );

      let updatedOptionsFromServer = [];
      try {
        const [updatedResults, createdResults] = await Promise.all([
          Promise.allSettled(updateTasks),
          Promise.allSettled(createTasks),
        ]);

        const ok = (r) => r.status === 'fulfilled';

        const updatedOk = updatedResults.filter(ok).map((r) => r.value?.data || r.value).filter(Boolean);
        const createdOk = createdResults.filter(ok).map((r) => r.value?.data || r.value).filter(Boolean);

        // Normalize returned items to our local shape
        const normalize = (o, fallback) => ({
          id: o?.questionOptionId ?? o?.optionId ?? o?.id ?? fallback?.id,
          questionOptionId: o?.questionOptionId ?? o?.optionId ?? o?.id ?? fallback?.questionOptionId,
          optionId: o?.optionId ?? o?.questionOptionId ?? o?.id ?? fallback?.optionId,
          questionId: o?.questionId ?? qId,
          optionText: o?.optionText ?? fallback?.optionText ?? '',
          isCorrect: typeof o?.isCorrect === 'boolean' ? o.isCorrect : Boolean(fallback?.isCorrect),
          order: Number(o?.order ?? fallback?.order ?? 0),
        });

        // Merge: prefer server responses when available; otherwise keep local edits
        const mergedExisting = existing.map((loc) => {
          const srv = updatedOk.find((x) => Number(x?.questionOptionId ?? x?.optionId ?? x?.id) === Number(loc?.id ?? loc?.questionOptionId ?? loc?.optionId));
          return normalize(srv || {}, loc);
        });
        const mergedCreated = createdOk.map((srv, idx) => normalize(srv, news[idx]));
        updatedOptionsFromServer = [...mergedExisting, ...mergedCreated]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      } catch (_) {
        // If anything fails, keep local options so UI still updates
        updatedOptionsFromServer = options.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      }

      // 3) Sync reducer so left list updates instantly
      dispatch({ type: actions.UPDATE_QUIZ_QUESTION, payload: { ...updatedQ, options: updatedOptionsFromServer } });
      setInfoAlert({ severity: 'success', message: 'Question and options updated successfully' });
      setOpenSnackBar(true);
      setEditMode(false);
      onUpdated?.({ question: { ...updatedQ, options: updatedOptionsFromServer } });
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to update question/options';
      // eslint-disable-next-line no-console
      console.error('UpdateOption save error:', error);
      setInfoAlert({ severity: 'error', message: String(message) });
      setOpenSnackBar(true);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    setForm(normalizedQ);
    setOptions(initialOptions);
    setEditMode(false);
  };

  return (
    <Box component="form" onSubmit={handleSave} sx={{ display: 'grid', gap: 1.5 }}>
      <Typography variant="h6">Edit Question and Options</Typography>

      <Stack direction="row" spacing={1.5}>
        <TextField size="small" select label="Question Type" value={form.questionType} onChange={onChangeQ('questionType')} sx={{ flex: 1 }} disabled={!editMode}>
          <MenuItem value="single_choice">Single choice</MenuItem>
          <MenuItem value="multiple_choice">Multiple choice</MenuItem>
          <MenuItem value="true_false">True / False</MenuItem>
          <MenuItem value="short_answer">Short answer</MenuItem>
        </TextField>
        <TextField
          size="small"
          select
          label="Related Skill"
          value={skills.some((s) => s.id === Number(form.skillId)) ? Number(form.skillId) : ''}
          onChange={onChangeQ('skillId')}
          disabled={!editMode || skillsLoading || !posteId}
          required
          error={Boolean(skillsError)}
          helperText={!posteId ? 'Select a Position to load requirements.' : skillsError ? 'Failed to load skills' : (skillsLoading ? 'Loading skills...' : (skills.length === 0 ? 'No skills found' : ' '))}
          sx={{ flex: 1 }}
        >
          {skills.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
        </TextField>
      </Stack>

      <TextField size="small" label="Question" value={form.questionText} onChange={onChangeQ('questionText')} required multiline minRows={2} disabled={!editMode} />

      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField size="small" label="Order" type="number" value={form.order} onChange={onChangeQ('order')} inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ width: 110 }} disabled={!editMode} />
        <TextField size="small" label="Points" type="number" value={form.points} onChange={onChangeQ('points')} inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ width: 110 }} disabled={!editMode} />
        <TextField size="small" label="Time Limit (sec)" type="number" value={form.timeLimit} onChange={onChangeQ('timeLimit')} inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ width: 130 }} disabled={!editMode} />
        <Box sx={{ flex: 1 }} />
        <FormControlLabel control={<Switch size="small" checked={form.isRequired} onChange={onChangeQ('isRequired')} disabled={!editMode} />} label="Required" />
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TextField size="small" label="Hint (optional)" value={form.hint} onChange={onChangeQ('hint')} multiline minRows={2} sx={{ flex: 1 }} disabled={!editMode} />
        <TextField size="small" label="Explanation (optional)" value={form.explanation} onChange={onChangeQ('explanation')} multiline minRows={2} sx={{ flex: 1 }} disabled={!editMode} />
      </Stack>

      <Divider />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Answer Options</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {optionsLoading && <CircularProgress size={16} />}
          {editMode && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addOption}>
              Add option
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack spacing={1}>
        {options.map((opt, idx) => (
          <Stack key={opt.id ?? idx} direction="row" alignItems="center" spacing={1}>
            <FormControlLabel control={<Switch size="small" checked={opt.isCorrect} onChange={(e) => updateOpt(idx, 'isCorrect', Boolean(e.target.checked))} disabled={!editMode} />} label={opt.isCorrect ? 'Correct' : 'Incorrect'} />
            <TextField size="small" placeholder={`Option ${idx + 1}...`} value={opt.optionText} onChange={(e) => updateOpt(idx, 'optionText', e.target.value)} fullWidth disabled={!editMode} />
            <TextField size="small" label="Order" type="number" value={opt.order} onChange={(e) => updateOpt(idx, 'order', Number(e.target.value))} inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ width: 80 }} disabled={!editMode} />
            {(opt.id || opt.questionOptionId) ? (
              <DeleteOption
                option={{ ...opt, questionOptionId: opt.questionOptionId ?? opt.id }}
                disabled={!editMode}
                onDeleted={({ optionId }) => removeOptById(optionId)}
              />
            ) : (
              <DeleteOption
                option={opt}
                disabled={!editMode}
                onDeleted={() => removeOptLocal(idx)}
              />
            )}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button size="small" variant="text" onClick={handleCancel}>Cancel</Button>
        <Button size="small" type="submit" variant="contained" disabled={savingQuestion || !form.questionText.trim()}>
          {savingQuestion ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>
    </Box>
  );
};

UpdateOption.propTypes = {
  question: PropTypes.shape({
    questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quizId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    skillId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    questionType: PropTypes.string,
    questionText: PropTypes.string,
    hint: PropTypes.string,
    points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    timeLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
    order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isRequired: PropTypes.bool,
    explanation: PropTypes.string,
  }).isRequired,
  posteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdated: PropTypes.func,
  onCancel: PropTypes.func,
};

export default UpdateOption;
